import { useEffect } from 'react';
import * as RadixToast from '@radix-ui/react-toast';
import { create } from 'zustand';
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from 'lucide-react';
import { cn } from '@shared/lib/cn';

/**
 * Lightweight toast system. Slides from top, hairline border, paper bg.
 * Auto-dismiss 3.5s; tap-to-dismiss anytime; optional action button.
 *
 * Usage: call `toast.success('Saved')` from anywhere — store-driven so it
 * works outside React, e.g. inside an axios interceptor.
 */

export type ToastVariant = 'success' | 'warning' | 'danger' | 'info';

export interface ToastEntry {
  id: number;
  variant: ToastVariant;
  title: string;
  description?: string;
  duration?: number;
}

interface ToastState {
  entries: ToastEntry[];
  push: (entry: Omit<ToastEntry, 'id'>) => number;
  dismiss: (id: number) => void;
}

const useToastStore = create<ToastState>((set) => ({
  entries: [],
  push(entry) {
    const id = Date.now() + Math.random();
    set((state) => ({ entries: [...state.entries, { ...entry, id }] }));
    return id;
  },
  dismiss(id) {
    set((state) => ({ entries: state.entries.filter((t) => t.id !== id) }));
  },
}));

/** Imperative toast API — usable from anywhere, no hooks required. */
export const toast = {
  success: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'success', title, description }),
  warning: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'warning', title, description }),
  danger: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'danger', title, description }),
  info: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'info', title, description }),
  dismiss: (id: number) => useToastStore.getState().dismiss(id),
};

const ICON: Record<ToastVariant, JSX.Element> = {
  success: <CheckCircle2 className="h-5 w-5 text-success" strokeWidth={1.75} />,
  warning: <AlertTriangle className="h-5 w-5 text-warning" strokeWidth={1.75} />,
  danger: <XCircle className="h-5 w-5 text-danger" strokeWidth={1.75} />,
  info: <Info className="h-5 w-5 text-info" strokeWidth={1.75} />,
};

/** Mount once near the root — typically inside <Providers>. */
export function ToastViewport(): JSX.Element {
  const entries = useToastStore((s) => s.entries);
  const dismiss = useToastStore((s) => s.dismiss);

  useEffect(() => {
    const timers = entries.map((entry) =>
      window.setTimeout(() => dismiss(entry.id), entry.duration ?? 3500),
    );
    return () => { for (const t of timers) clearTimeout(t); };
  }, [entries, dismiss]);

  return (
    <RadixToast.Provider duration={3500} swipeDirection="up">
      {entries.map((entry) => (
        <RadixToast.Root
          key={entry.id}
          open
          onOpenChange={(open) => { if (!open) dismiss(entry.id); }}
          className={cn(
            'group relative flex items-start gap-3 rounded-lg bg-card hairline shadow-elevated px-4 py-3 paper-grain',
            'data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:slide-in-from-top-3',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out',
          )}
        >
          {ICON[entry.variant]}
          <div className="flex-1 min-w-0">
            <RadixToast.Title className="text-body font-semibold text-ink">
              {entry.title}
            </RadixToast.Title>
            {entry.description ? (
              <RadixToast.Description className="text-body-sm text-ink-secondary mt-0.5">
                {entry.description}
              </RadixToast.Description>
            ) : null}
          </div>
          <RadixToast.Close className="text-ink-muted hover:text-ink-secondary transition-colors" aria-label="Dismiss">
            <X className="h-4 w-4" strokeWidth={1.75} />
          </RadixToast.Close>
        </RadixToast.Root>
      ))}
      <RadixToast.Viewport className="fixed top-safe inset-x-0 z-[100] flex flex-col items-stretch gap-2 px-6 pt-3 pointer-events-none [&>*]:pointer-events-auto" />
    </RadixToast.Provider>
  );
}
